import { CornerTable } from "../../v2/components/CornerTable.jsx";
import { TABLE_TOP_CORNERS } from "../../v2/components/tableHeaderCorners.js";
// src/features/plan/MarketPressurePage.jsx
//
// MARKET-PRESSURE-S1 — Forward headcount vs. capacity pressure heatmap.
// MARKET-PRESSURE-S2 — Master-detail panel, per-office sub-rows.
// MARKET-PRESSURE-S3 — Layer toggle (as-committed vs. raw), one-time-move
//                       annotation, delta callout, and hand-off buttons.
//
// Answers: "Where does the plan break if we change nothing today?"
//
// RAG verdicts:
//   over                — projected HC ≥ capacity  (red)
//   tight               — projected HC ≥ 90% capacity  (amber)
//   covered             — below threshold  (green)
//   attendance_unknown  — no utilization data (grey, usable basis only)
//   insufficient_history — < 3 monthly headcount snapshots (grey)
//
// Controls:
//   Layer    [As-committed · Raw]  (default as-committed)
//   Capacity [Usable · Seats]      (default Usable)
//   Horizon  [12 · 18 · 24 mo]    (default 18)
//
// Expandable rows: click a market row to reveal per-office breakdown.
// Clickable rows:  click a market name to open the detail panel.
//
// Data: GET /api/plan/market-pressure?horizon=N&basis=usable|seats&layer=as-committed|raw
// Client: getMarketPressure(horizon, basis, layer) in marketsClient.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { scopeMarketPressure } from "../../shared/marketPressureSummary.js";
import { T } from "../../app/tokens.js";
import { getMarketPressure } from "../../data/marketsClient.js";
import { computePortfolioKpis } from "./marketsService.js";
import { fmtKpiUsd } from "../../utils/fmtAbbreviated.js";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiTileRow, KPI_TILE_ROW_HEIGHT } from "./KpiTileRow.jsx";
import { log } from "../../utils/logger.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { PRESSURE_CELL_STYLES as RAG_STYLE, PressureViewOptions, PressureCapacityOptions, PressureViewKey } from "./PressureViewControls.jsx";
import { MarketPressureDrawer } from "./MarketPressureDrawer.jsx";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Format an ISO date string ("YYYY-MM-DD") as "Month YYYY" (e.g. "Jul 2026").
 * Returns null when the input is absent or unparseable.
 */
function fmtAttendanceMonth(monthStr) {
  if (!monthStr) return null;
  const d = new Date(String(monthStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

// ─── RAG palette — all colors via T.* tokens (no raw hex per L52) ─────────────

// ─── RAG Cell ─────────────────────────────────────────────────────────────────

function RagCell({ verdict, projectedHc, capacityBasis, overOfficeCount, tightOfficeCount, officeCount, testId }) {
  const s = RAG_STYLE[verdict] ?? RAG_STYLE.insufficient_history;
  const hasNumbers =
    typeof projectedHc === "number" &&
    typeof capacityBasis === "number" &&
    capacityBasis > 0;

  // When the market verdict is "over" (or "tight") but the aggregate ratio appears
  // covered (projectedHc < capacityBasis), replace the number ratio with a plain-
  // English count so the label and sub-line are self-consistent.
  const aggregateAppearsCovered = hasNumbers && projectedHc < capacityBasis;
  let subLine = null;
  if (
    hasNumbers &&
    verdict !== "insufficient_history" &&
    verdict !== "attendance_unknown"
  ) {
    if (verdict === "over" && aggregateAppearsCovered && typeof overOfficeCount === "number" && typeof officeCount === "number") {
      subLine = `${overOfficeCount} of ${officeCount} offices over`;
    } else if (verdict === "tight" && aggregateAppearsCovered && typeof tightOfficeCount === "number" && typeof officeCount === "number") {
      subLine = `${tightOfficeCount} of ${officeCount} offices tight`;
    } else {
      subLine = `${projectedHc} / ${capacityBasis}`;
    }
  }

  return (
    <td
      data-testid={testId}
      data-verdict={verdict}
      title={hasNumbers ? `${projectedHc} HC / ${capacityBasis} capacity` : s.label}
      style={{
        padding:       "6px 8px",
        minWidth:      64,
        overflow:      "hidden",
        verticalAlign: "middle",
        textAlign:     "center",
      }}
    >
      <div
        style={{
          display:        "flex",
          flexDirection:  "column",
          alignItems:     "center",
          justifyContent: "center",
          background:     s.bg,
          border:         `1px solid ${s.border}`,
          borderRadius:   7,
          padding:        "3px 8px 4px",
          width:          "100%",
          boxSizing:      "border-box",
        }}
      >
        <span
          style={{
            fontSize:   T.fsXs,
            fontWeight: T.fwBold,
            color:      s.text,
            whiteSpace: "nowrap",
            lineHeight: 1.3,
          }}
        >
          {s.label}
        </span>
        {subLine && (
          <div style={{ fontSize: T.fsXxs, color: s.text, opacity: 0.75, lineHeight: 1.2, whiteSpace: "nowrap" }}>
            {subLine}
          </div>
        )}
      </div>
    </td>
  );
}

// ─── Expand button ────────────────────────────────────────────────────────────

function ExpandButton({ expanded, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-expanded={expanded}
      style={{
        background:   "none",
        border:       0,
        cursor:       "pointer",
        padding:      "0 4px",
        color:        T.mid,
        fontSize:     T.fsXs,
        lineHeight:   1,
        verticalAlign: "middle",
      }}
    >
      {expanded ? "▾" : "▸"}
    </button>
  );
}

// ─── Office sub-row ───────────────────────────────────────────────────────────

function OfficeSubRow({ office, colCount }) {
  return (
    <tr data-testid={`office-row-${office.locationId}`}>
      <td
        style={{
          paddingLeft:  32,
          paddingRight: 8,
          paddingTop:   5,
          paddingBottom: 5,
          fontSize:     T.fsXs,
          color:        T.mid,
          whiteSpace:   "nowrap",
          overflow:     "hidden",
          textOverflow: "ellipsis",
          maxWidth:     160,
          borderBottom: `1px solid ${T.hair}`,
        }}
        title={office.locationAlias}
      >
        {office.locationAlias}
        {office.oneTimeMoveDetected && (
          <span
            data-testid={`one-time-move-badge-${office.locationId}`}
            title="One-time move (consolidation or relocation): this headcount jump is excluded from the growth projection to avoid inflating the demand signal."
            style={{
              display:      "inline-block",
              background:   T.blueBg,
              color:        T.blue,
              border:       `1px solid ${T.blue}`,
              borderRadius: 12,
              padding:      "1px 6px",
              fontSize:     T.fsXxs,
              fontWeight:   T.fwSemibold,
              marginLeft:   5,
            }}
          >
            one-time
          </span>
        )}
        {office.installedSeats != null && (
          <span style={{ marginLeft: 6, fontSize: T.fsXxs, color: T.light }}>
            {office.installedSeats} seats
            {office.peakAttendPct != null && ` · ${office.peakAttendPct}% peak`}
          </span>
        )}
        {office.attendanceAsOfMonth != null && (
          <span
            data-testid={`office-attendance-month-${office.locationId}`}
            style={{ marginLeft: 6, fontSize: T.fsXxs, color: T.light }}
          >
            {`As of ${fmtAttendanceMonth(office.attendanceAsOfMonth)}`}
          </span>
        )}
      </td>
      {(office.cells ?? []).map((cell, qi) => (
        <RagCell
          key={qi}
          verdict={cell.verdict}
          projectedHc={cell.projectedHc}
          capacityBasis={cell.capacityBasis}
          testId={`office-cell-${office.locationId}-${qi}`}
        />
      ))}
      {Array.from({ length: Math.max(0, colCount - (office.cells?.length ?? 0)) }).map((_, i) => (
        <td key={`pad-${i}`} style={{ borderBottom: `1px solid ${T.hair}` }} />
      ))}
    </tr>
  );
}

// ─── Heatmap table ────────────────────────────────────────────────────────────

function PressureHeatmap({
  markets,
  quarters,
  expandedMarkets,
  onToggleExpand,
  altVerdictMap,
  selectedMarketId,
  onSelectMarket,
}) {
  const headerRef = useRef(null);
  if (!markets || markets.length === 0) {
    return (
      <div style={{ padding: "32px 0", color: T.mid, fontSize: T.fsSm }}>
        No active markets to display.
      </div>
    );
  }

  const colCount = quarters.length;
  const tableMinWidth = 168 + colCount * 80;

  return (
    <div data-testid="market-pressure-heatmap" style={TABLE_TOP_CORNERS}>
      <div data-testid="pressure-column-header" style={{ ...TABLE_TOP_CORNERS, position: "sticky", top: "var(--places-sticky-top, 0px)", zIndex: 4, background: T.tableHeadBg }}>
      <div ref={headerRef} style={{ overflowX: "hidden", ...TABLE_TOP_CORNERS }}>
      <CornerTable
        style={{
          borderCollapse: "collapse",
          width:          "100%",
          tableLayout:    "fixed",
          minWidth:       tableMinWidth,
        }}
      >
        <colgroup>
          <col style={{ width: 168 }} />
          {quarters.map((q) => (
            <col key={q.label} style={{ width: 80 }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th
              style={{
                textAlign:     "left",
                padding:       "6px 8px",
                fontSize:      T.fsXxs,
                color:         T.mid,
                fontWeight:    T.fwSemibold,
                background:    T.tableHeadBg,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                borderBottom:  `1px solid ${T.hair}`,
              }}
            >
              Market
            </th>
            {quarters.map((q) => (
              <th
                key={q.label}
                data-testid={`quarter-header-${q.label}`}
                style={{
                  textAlign:     "center",
                  padding:       "6px 4px",
                  fontSize:      T.fsXxs,
                  color:         T.mid,
                  fontWeight:    T.fwSemibold,
                  whiteSpace:    "nowrap",
                  background:    T.tableHeadBg,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  borderBottom:  `1px solid ${T.hair}`,
                }}
              >
                {q.label}
              </th>
            ))}
          </tr>
        </thead>
      </CornerTable>
      </div>
      </div>
      <div style={{ overflowX: "auto" }} onScroll={(event) => {
        if (headerRef.current) headerRef.current.scrollLeft = event.currentTarget.scrollLeft;
      }}>
      <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed", minWidth: tableMinWidth }}>
        <colgroup>
          <col style={{ width: 168 }} />
          {quarters.map((q) => <col key={q.label} style={{ width: 80 }} />)}
        </colgroup>
        <tbody>
          {markets.map((market) => {
            const isExpanded  = expandedMarkets.has(market.id);
            const isSelected  = selectedMarketId === market.id;
            const hasOffices  = (market.offices ?? []).length > 1;
            const altVerdict  = altVerdictMap?.get(market.id);
            const verdictDiff = altVerdict != null && altVerdict !== market.rollupVerdict;
            return (
              <React.Fragment key={market.id}>
                <tr
                  data-testid={`market-row-${market.id}`}
                  onClick={() => onSelectMarket?.(market.id)}
                  style={{ cursor: "pointer", borderBottom: `1px solid ${T.hair}` }}
                >
                  <td
                    style={{
                      padding:      "7px 8px",
                      fontSize:     T.fsSm5,
                      fontWeight:   T.fwSemibold,
                      color:        isSelected ? T.blue : T.ink,
                      background:   isSelected ? T.blueBg : "transparent",
                      whiteSpace:   "nowrap",
                      overflow:     "hidden",
                      textOverflow: "ellipsis",
                      textAlign:    "left",
                    }}
                    title={market.name}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 18, flexShrink: 0, display: "inline-flex", alignItems: "center" }}>
                        {hasOffices && (
                          <ExpandButton
                            expanded={isExpanded}
                            onClick={(e) => { e.stopPropagation(); onToggleExpand(market.id); }}
                            testId={`expand-market-${market.id}`}
                          />
                        )}
                      </span>
                      <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                        <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {market.name}
                          </span>
                          {verdictDiff && (
                            <span
                              data-testid={`layer-diff-indicator-${market.id}`}
                              title={`Verdict differs between layers (alt: ${altVerdict})`}
                              style={{
                                display:      "inline-block",
                                width:        7,
                                height:       7,
                                borderRadius: "50%",
                                background:   T.ragAmberText,
                                marginLeft:   5,
                                flexShrink:   0,
                              }}
                            />
                          )}
                        </div>
                        {market.totalPeople != null && (
                          <div
                            style={{
                              fontSize:   T.fsXxs,
                              color:      T.light,
                              fontWeight: T.fwRegular,
                              marginTop:  1,
                              whiteSpace: "nowrap",
                              overflow:   "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {market.totalPeople} ppl · {market.totalSeats ?? "?"} seats
                            {market.peakAttendPct != null && ` · ${market.peakAttendPct}% peak`}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  {market.quarters.map((cell, qi) => (
                    <RagCell
                      key={qi}
                      verdict={cell.verdict}
                      projectedHc={cell.projectedHc}
                      capacityBasis={cell.capacityBasis}
                      overOfficeCount={cell.overOfficeCount}
                      tightOfficeCount={cell.tightOfficeCount}
                      officeCount={cell.officeCount}
                      testId={`cell-${market.id}-${qi}`}
                    />
                  ))}
                </tr>
                {isExpanded &&
                  (market.offices ?? []).map((office) => (
                    <OfficeSubRow
                      key={office.locationId}
                      office={office}
                      colCount={colCount}
                    />
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ─── Delta callout ────────────────────────────────────────────────────────────
// Shows how many over-capacity markets the committed plan resolves vs. raw.

function DeltaCallout({ resolvedCount }) {
  if (!resolvedCount || resolvedCount <= 0) return null;
  return (
    <div
      data-testid="delta-callout"
      style={{
        display:      "flex",
        alignItems:   "flex-start",
        gap:          10,
        padding:      "9px 12px",
        background:   T.ragGreenBg,
        border:       `1px solid ${T.ragGreenBorder}`,
        borderRadius: 6,
        fontSize:     T.fsSm5,
        color:        T.ragGreenText,
        marginBottom: 8,
      }}
    >
      <span style={{ lineHeight: 1, marginTop: 1 }}>✓</span>
      <span>
        The committed plan resolves{" "}
        <strong>
          {resolvedCount} market{resolvedCount !== 1 ? "s" : ""}
        </strong>{" "}
        that would be Over under raw status quo.
      </span>
    </div>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────
// Re-exported for backward compatibility; implementation lives in MarketPressureDrawer.jsx.
const DetailPanel = MarketPressureDrawer;

// ─── Main component ────────────────────────────────────────────────────────────

// ─── Exported KPI strip — used by MarketPlanPage to hoist the tiles above the
// shell toolbar.
//
// KPI-SCOPE-INDICATOR (G-22): tile set reworked from the old pressure-data tiles
// (Action now / Watch / Covered) to match the plan-wide scope indicator spec:
//   Responsive (left 5): Markets · Annual cost · Markets over capacity ·
//                        Portfolio balance · Next wall
//   Portfolio (right):   Assignment ratio
//
// The strip receives:
//   markets        — filtered market list (plan data) for the 5 responsive tiles.
//   allMarkets     — canonical active markets for the portfolio ratio tile.
//   isFiltered     — true when any search/filter is active; tiles turn blue.
//   nextWallQuarter — pre-computed "Q1 20XX" string from the parent
//                     (computed there because quarterFromDate is local to
//                     MarketPlanPage); "—" when no upcoming lease wall found.
export function MarketPressureKpiStrip({
  markets       = [],
  allMarkets    = [],
  isFiltered    = false,
  nextWallQuarter = "—",
}) {
  const kpis      = useMemo(() => computePortfolioKpis(markets),    [markets]);
  const portKpis  = useMemo(() => computePortfolioKpis(allMarkets), [allMarkets]);

  const balanceLabel  = `${kpis.balance >= 0 ? "+" : ""}${Math.round(kpis.balance)}`;
  const balanceColor  = kpis.balance < 0 ? T.ragRed : T.greenDeep;
  const overCapColor  = kpis.overCapacityCount > 0 ? T.ragRed : T.dark;
  const assignPct     = portKpis.peakUtilPct != null ? `${Math.round(portKpis.peakUtilPct)}%` : "—";
  const assignColor   = portKpis.peakUtilPct > 100 ? T.ragRed : (portKpis.peakUtilPct >= 90 ? T.ragAmber : "var(--v2-green)");

  return (
    <KpiTileRow>
      <KpiTile
        testId="mp-kpi-markets"
        label="Markets"
        descriptor="distinct markets"
        value={String(kpis.marketsCount)}
        rawValue={kpis.marketsCount}
        color={T.blue}
        height={KPI_TILE_ROW_HEIGHT}
        tileIndex={0}
        scope="responsive"
        isFiltered={isFiltered}
      />
      <KpiTile
        testId="mp-kpi-annual-cost"
        label="Annual cost"
        descriptor="all-in, fully-loaded"
        value={kpis.totalAnnualCost != null ? fmtKpiUsd(kpis.totalAnnualCost) : "—"}
        rawValue={kpis.totalAnnualCost ?? undefined}
        formatter={fmtKpiUsd}
        color="var(--v2-green)"
        height={KPI_TILE_ROW_HEIGHT}
        tileIndex={1}
        scope="responsive"
        isFiltered={isFiltered}
      />
      <KpiTile
        testId="mp-kpi-over-capacity"
        label="Markets over capacity"
        descriptor="assigned exceeds usable seats"
        value={String(kpis.overCapacityCount)}
        rawValue={kpis.overCapacityCount}
        color={overCapColor}
        height={KPI_TILE_ROW_HEIGHT}
        tileIndex={2}
        scope="responsive"
        isFiltered={isFiltered}
      />
      <KpiTile
        testId="mp-kpi-portfolio-balance"
        label="Portfolio balance"
        descriptor="usable seats minus assigned HC"
        value={balanceLabel}
        color={balanceColor}
        height={KPI_TILE_ROW_HEIGHT}
        tileIndex={3}
        scope="responsive"
        isFiltered={isFiltered}
      />
      <KpiTile
        testId="mp-kpi-next-wall"
        label="Next wall"
        descriptor="nearest lease expiry or break"
        value={nextWallQuarter}
        color={T.ragAmber}
        height={KPI_TILE_ROW_HEIGHT}
        tileIndex={4}
        scope="responsive"
        isFiltered={isFiltered}
      />
      <KpiTile
        testId="mp-kpi-assignment-ratio"
        label="Assignment ratio"
        descriptor="assigned HC vs usable seats"
        value={assignPct}
        rawValue={portKpis.peakUtilPct ?? undefined}
        formatter={(n) => `${Math.round(n)}%`}
        color={assignColor}
        height={KPI_TILE_ROW_HEIGHT}
        tileIndex={5}
        scope="portfolio"
      />
    </KpiTileRow>
  );
}

// Reporting loading state to a parent must not allocate a new KPI identity on
// every render, or the parent/child notification cycle never settles.
const EMPTY_PRESSURE_KPIS = Object.freeze({});

export function MarketPressurePage(props) {
  // Scope is an identity boundary, not an optional/removable search filter.
  return <MarketPressureContent key={props.scopeMarketId ?? "portfolio"} {...props} />;
}

function MarketPressureContent({ scopeMarketId = null, onOpenMarketPlan, onTestScenario, suppressKpiStrip = false, onKpisChange, onCollectionCountsChange, searchQuery = "", pressureSortKey = "market", pressureSortDir = "asc", toolbarTargets }) {
  const [horizon, setHorizon]               = useState(18);
  const [basis, setBasis]                   = useState("usable");
  const [layer, setLayer]                   = useState("as-committed");
  const [layerData, setLayerData]           = useState({ "as-committed": null, "raw": null });
  const [loading, setLoading]               = useState(true);
  const [fetchError, setFetchError]         = useState(null);
  const [expandedMarkets, setExpandedMarkets] = useState(() => new Set());
  const [selectedMarketId, setSelectedMarketId] = useState(null);

  // Always fetch both layers in parallel so the delta callout and layer-diff
  // indicators are available immediately on toggle without a second round-trip.
  const load = useCallback(async (h, b) => {
    setLoading(true);
    setFetchError(null);
    try {
      const [committed, raw] = await Promise.all([
        getMarketPressure(h, b, "as-committed"),
        getMarketPressure(h, b, "raw"),
      ]);
      setLayerData({ "as-committed": committed, "raw": raw });
    } catch (err) {
      setFetchError(err?.message ?? "Failed to load market pressure data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(horizon, basis); }, [horizon, basis, load]);

  // Clear market selection when controls change so panel doesn't show stale data.
  useEffect(() => { setSelectedMarketId(null); }, [horizon, basis, layer]);

  // Notify parent (MarketPlanPage) when KPI data changes so the hoisted strip
  // above the shell toolbar can stay in sync.  Only fires when suppressKpiStrip
  // is requested — zero overhead in standalone usage.
  const scopedLayers = useMemo(() => ({
    raw: scopeMarketPressure(layerData.raw, scopeMarketId),
    "as-committed": scopeMarketPressure(layerData["as-committed"], scopeMarketId),
  }), [layerData, scopeMarketId]);
  const data    = scopedLayers[layer];
  const altData = scopedLayers[layer === "as-committed" ? "raw" : "as-committed"];

  const kpis     = data?.kpis ?? EMPTY_PRESSURE_KPIS;
  const quarters = data?.quarters ?? [];
  const markets  = data?.markets  ?? [];
  // PEAK-DATA-MOST-RECENT-AVAILABLE: most-recent month with badge data.
  const attendanceAsOfMonth = data?.attendanceAsOfMonth ?? null;

  // ── Alert messages — computed here so they can be surfaced to the parent
  // toolbar via onKpisChange (Pressure AlertChip lives in the shared toolbar).
  const falseAlarmStats = kpis.falseAlarmStats;
  const falseAlarmDelta = (falseAlarmStats?.seatsFlagged ?? 0) - (falseAlarmStats?.usableFlagged ?? 0);
  const alertMessages = [];
  if (!loading && !fetchError && kpis.nextWall) {
    alertMessages.push({
      id:       "next-wall",
      severity: "urgent",
      text:     `Next wall: ${kpis.nextWall.quarter} — ${kpis.nextWall.markets.slice(0, 3).join(", ")}${kpis.nextWall.marketCount > 3 ? ` +${kpis.nextWall.marketCount - 3} more` : ""}`,
    });
  }
  if (!loading && !fetchError && basis === "usable" && falseAlarmDelta > 0 && typeof falseAlarmStats?.usableFlagged === "number") {
    alertMessages.push({
      id:       "false-alarm",
      severity: "warn",
      text:     `Attendance-adjusted flags ${falseAlarmStats.usableFlagged} vs ${falseAlarmStats.seatsFlagged} — ${falseAlarmDelta} fewer false alarm${falseAlarmDelta !== 1 ? "s" : ""}`,
    });
  }

  // Keep the hoisted KPI strip (rendered by MarketPlanPage above the toolbar)
  // in sync with local state.  Called whenever the derived kpis, loading flag,
  // or any control that affects the animation key changes.  `quarters` is
  // included so MarketPlanPage can derive the per-view Pressure sort fields.
  useEffect(() => {
    onKpisChange?.({ kpis, loading, horizon, basis, layer, marketsCount: markets.length, alertMessages, quarters });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpis, loading, horizon, basis, layer, markets.length, quarters.map((q) => q.label).join(",")]);

  // ── Delta callout: markets the committed plan resolves vs. raw ──────────────
  const rawMarkets       = scopedLayers["raw"]?.markets ?? [];
  const committedMarkets = scopedLayers["as-committed"]?.markets ?? [];
  const resolvedByCommitted = rawMarkets.filter((rawM) => {
    const isOverRaw = rawM.quarters.some((q) => q.verdict === "over");
    if (!isOverRaw) return false;
    const committedM     = committedMarkets.find((cm) => cm.id === rawM.id);
    const isOverCommitted = committedM?.quarters.some((q) => q.verdict === "over") ?? true;
    return !isOverCommitted;
  }).length;

  // ── Per-market alt-layer verdict map for diff indicator ─────────────────────
  const altVerdictMap = new Map(
    (altData?.markets ?? []).map((m) => [m.id, m.rollupVerdict])
  );

  // ── Search filter — client-side name match (case-insensitive) ──────────────
  const searchedMarkets = searchQuery.trim()
    ? markets.filter((m) => matchesFoldedQuery(searchQuery, m.name))
    : markets;
  const countsCallback = useRef(onCollectionCountsChange);
  countsCallback.current = onCollectionCountsChange;
  useEffect(() => {
    countsCallback.current?.({ count: searchedMarkets.length, total: markets.length });
  }, [searchedMarkets.length, markets.length]);

  // ── Pressure sort — applied after search filter ──────────────────────────
  const filteredMarkets = useMemo(() => {
    const arr = searchedMarkets.slice();
    if (pressureSortKey === "market") {
      arr.sort((a, b) => {
        const cmp = a.name.localeCompare(b.name);
        return pressureSortDir === "asc" ? cmp : -cmp;
      });
    } else if (pressureSortKey.startsWith("q_")) {
      const label = pressureSortKey.slice(2);
      arr.sort((a, b) => {
        const qA = a.quarters?.find((q) => q.quarterLabel === label);
        const qB = b.quarters?.find((q) => q.quarterLabel === label);
        const ratioA = qA && qA.capacityBasis ? qA.projectedHc / qA.capacityBasis : 0;
        const ratioB = qB && qB.capacityBasis ? qB.projectedHc / qB.capacityBasis : 0;
        const rA = isFinite(ratioA) ? ratioA : 0;
        const rB = isFinite(ratioB) ? ratioB : 0;
        // Default for numeric: desc = highest pressure first
        return pressureSortDir === "desc" ? rB - rA : rA - rB;
      });
    }
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedMarkets, pressureSortKey, pressureSortDir]);

  const selectedMarket = filteredMarkets.find((m) => m.id === selectedMarketId) ?? null;

  const handleToggleExpand = useCallback((marketId) => {
    setExpandedMarkets((prev) => {
      const next = new Set(prev);
      if (next.has(marketId)) {
        next.delete(marketId);
      } else {
        next.add(marketId);
      }
      return next;
    });
  }, []);

  const handleSelectMarket = useCallback((marketId) => {
    setSelectedMarketId((prev) => (prev === marketId ? null : marketId));
  }, []);

  const eyebrowLayer = layer === "as-committed" ? "as-committed" : "raw status quo";
  const options = <PressureViewOptions {...{ layer, basis, horizon, setLayer, setBasis, setHorizon }} collapseCapacity={toolbarTargets?.collapseCapacity} />;

  return (
    <div data-testid="market-pressure-page" style={{ paddingBottom: 48 }}>
      {/* Portals preserve child-local state/lifecycle while the parent owns layout.
          No callback/effect mirrors controls back and forth between components. */}
      {toolbarTargets ? <>
        {toolbarTargets.options && createPortal(options, toolbarTargets.options)}
        {toolbarTargets.key && createPortal(<PressureViewKey />, toolbarTargets.key)}
        {toolbarTargets.collapseCapacity && toolbarTargets.capacity && createPortal(<PressureCapacityOptions basis={basis} setBasis={setBasis} />, toolbarTargets.capacity)}
      </> : <div data-testid="pressure-local-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        {options}<PressureViewKey />
      </div>}

      {/* ── KPI strip ─────────────────────────────────────────────────────── */}
      {/* Suppressed when the parent (MarketPlanPage) hoists the strip above the
          shell toolbar via suppressKpiStrip={true}.  Rendered inline in all
          other contexts (standalone usage, tests).  In standalone mode no plan
          data is available, so we pass empty lists; the strip shows zeros/dashes
          which is acceptable since this path is only used in tests/storybooks. */}
      {scopeMarketId && !loading && !fetchError && (
        <div data-testid="market-pressure-scope-summary" style={{ color: T.mid, marginBottom: 12 }}>
          {markets.length === 0 ? "No pressure data for this market." : (
            <>
              {markets[0].name}: {markets[0].totalPeople ?? "Unknown"} people / {markets[0].totalSeats ?? "unknown"} seats.
              {" "}Action now: {kpis.actionNow}; watch next 12 months: {kpis.watchNext12Mo}; covered: {kpis.covered}.
              {" "}Next wall: {kpis.nextWall?.quarter ?? "None forecast"}.
            </>
          )}
        </div>
      )}
      {!scopeMarketId && !suppressKpiStrip && (
        <MarketPressureKpiStrip
          markets={[]}
          allMarkets={[]}
          isFiltered={false}
          nextWallQuarter="—"
        />
      )}

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      {loading && (
        <div style={{ color: T.light, fontSize: T.fsSm, padding: "24px 0" }}>
          Loading market pressure…
        </div>
      )}
      {!loading && fetchError && (
        <div style={{ color: T.ragRed, fontSize: T.fsSm, padding: "24px 0" }}>
          {fetchError}
        </div>
      )}
      {!loading && !fetchError && (
        <>
          {/* ── Delta callout (raw layer: committed plan resolves N markets) ── */}
          {layer === "raw" && (
            <DeltaCallout resolvedCount={resolvedByCommitted} />
          )}

          {/* ── Heatmap wrapped in panel card + detail panel ───────────────── */}
          <div
            style={{
              display:             "grid",
              gridTemplateColumns: selectedMarket ? "minmax(0, 1fr) 360px" : "minmax(0, 1fr)",
              gap:                 16,
              alignItems:          "flex-start",
            }}
          >
            <div
              data-testid="mp-heatmap-card"
              style={{
                background:   T.card,
                border:       `1px solid ${T.border}`,
                borderRadius: 12,
                boxShadow:    "0 1px 4px rgba(16,36,58,.06)",
                minWidth:     0,
              }}
            >
              <div data-testid="mp-eyebrow" style={{ padding: "12px 16px", borderBottom: `1px solid ${T.hair}`, fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.mid, textTransform: "uppercase", letterSpacing: ".04em" }}>
                Baseline forecast · {eyebrowLayer}
              </div>
              <PressureHeatmap
                markets={filteredMarkets}
                quarters={quarters}
                expandedMarkets={expandedMarkets}
                onToggleExpand={handleToggleExpand}
                altVerdictMap={altVerdictMap}
                selectedMarketId={selectedMarketId}
                onSelectMarket={handleSelectMarket}
              />
            </div>
            {selectedMarket && (
              <DetailPanel
                market={selectedMarket}
                onOpenMarketPlan={onOpenMarketPlan ?? (() => {})}
                onTestScenario={onTestScenario ?? (() => {})}
                layer={layer}
                basis={basis}
                horizon={horizon}
                onClose={() => setSelectedMarketId(null)}
              />
            )}
          </div>
        </>
      )}

      {/* ── Disclosure footnote ────────────────────────────────────────────── */}
      {!loading && !fetchError && (
        <p
          style={{
            marginTop:  24,
            fontSize:   T.fsXs,
            color:      T.light,
            lineHeight: 1.5,
          }}
        >
          Projections use a linear trend over the last 6 monthly headcount
          snapshots. "Usable capacity" scales installed seats by the office's
          observed peak attendance rate. Markets with fewer than 3 snapshots
          show "—" and are excluded from breach counts.
          {layer === "raw" && (
            <>
              {" "}Raw layer holds capacity at today's installed seats for all quarters;
              as-committed follows the capacity schedule.
            </>
          )}
          {basis === "usable" && attendanceAsOfMonth && (
            <>
              {" "}
              <span data-testid="mp-attendance-as-of">
                As of {fmtAttendanceMonth(attendanceAsOfMonth)}
              </span>
            </>
          )}
        </p>
      )}
    </div>
  );
}
